// 우측 통합 패널(스크리닝 결과 ↔ 파일 미리보기) 상태 — 순수 리듀서.
// ChatPage 가 { open, tab } 만 들고, 실제 내용 유무(hasResults/hasPreview)는
// rpSpecs/previewFile 에서 파생해 이벤트에 실어 보낸다. 표시 여부·표시 탭은
// isPanelVisible/effectiveTab 파생 함수로 계산한다 (탭에 내용이 없으면 있는 쪽 보정).
export type RightPanelTab = "results" | "preview";

export interface RightPanelState {
  open: boolean;
  tab: RightPanelTab;
}

/** 내용 유무 — rpSpecs.length / previewFile 에서 파생 */
export interface RightPanelContent {
  hasResults: boolean;
  hasPreview: boolean;
}

export const RIGHT_PANEL_CLOSED: RightPanelState = { open: false, tab: "results" };

export type RightPanelEvent =
  /** screen_panel 도착·채팅 '전체 결과' 버튼 — 자동 오픈 + 결과 탭 활성 */
  | { type: "results-arrived" }
  /** 미리보기 열기(아티팩트 미리보기·문서함 보기·엑셀 바로 보기) — 오픈 + 미리보기 탭 */
  | { type: "preview-opened" }
  /** 미리보기 닫기(내용 제거) — 결과가 있으면 결과 탭으로, 없으면 패널 닫힘 */
  | { type: "preview-closed"; hasResults: boolean }
  /** 탑바 격자 아이콘 — 내용이 하나도 없으면 무시, 있으면 열고/닫기 */
  | { type: "toggle"; content: RightPanelContent }
  /** 탭 바 클릭 */
  | { type: "set-tab"; tab: RightPanelTab }
  /** 패널 닫기(X)·문서함 진입·정보 패널 토글 — 내용은 유지 */
  | { type: "close" };

/** 요청 탭에 내용이 없으면 있는 쪽으로 보정 — 둘 다 없으면 null(표시 불가) */
export function effectiveTab(
  tab: RightPanelTab,
  content: RightPanelContent,
): RightPanelTab | null {
  if (!content.hasResults && !content.hasPreview) return null;
  if (tab === "results") return content.hasResults ? "results" : "preview";
  return content.hasPreview ? "preview" : "results";
}

/** 패널이 실제로 그려져야 하는가 — 열림 + 표시할 내용 존재 */
export function isPanelVisible(
  state: RightPanelState,
  content: RightPanelContent,
): boolean {
  return state.open && effectiveTab(state.tab, content) !== null;
}

/** 탭 바 노출 여부 — 두 내용이 모두 있을 때만 (하나면 기존 단독 패널 모습) */
export function showTabBar(content: RightPanelContent): boolean {
  return content.hasResults && content.hasPreview;
}

export function rightPanelReduce(
  state: RightPanelState,
  ev: RightPanelEvent,
): RightPanelState {
  switch (ev.type) {
    case "results-arrived":
      return { open: true, tab: "results" };
    case "preview-opened":
      return { open: true, tab: "preview" };
    case "preview-closed":
      // 미리보기 내용이 사라진다 — 결과가 있으면 그 탭으로 남고, 없으면 닫는다
      return ev.hasResults ? { open: state.open, tab: "results" } : RIGHT_PANEL_CLOSED;
    case "toggle": {
      if (state.open) return { ...state, open: false };
      const t = effectiveTab(state.tab, ev.content);
      if (t === null) return state; // 표시할 내용 없음 — 무시 (버튼도 hidden)
      return { open: true, tab: t };
    }
    case "set-tab":
      return { ...state, tab: ev.tab };
    case "close":
      return { ...state, open: false };
  }
}
